import { ReactNode } from "react";
import { ExtractRouteParams, matchPath, RedirectProps, Switch } from "react-router";
import { generatePath, Redirect, Route, RouteComponentProps, RouteProps } from "react-router-dom";

import { isSubLoggedIn, useAuth } from "~components";
import { Models } from "~utils";

import { UNAUTHENTICATED_ROUTES } from "../unauthenticated/Routes";
import { MorePage, TasksPage } from "./[onboardingConfigurationId]";
import { TaskPage } from "./[onboardingConfigurationId]/[task]/[step]";
import { RejectionPage } from "./[onboardingConfigurationId]/rejection";

/***** Constants *****/
export const ONBOARDING_ROUTES = {
  tasks: "/onboarding/:onboardingConfigurationId",
  rejection: "/onboarding/:onboardingConfigurationId/rejection",
  task: "/onboarding/:onboardingConfigurationId/:task/:step?",
  more: "/onboarding/:onboardingConfigurationId/more",
} as const;

/***** Component *****/
/**
 * Entry point for the onboarding experience. This components responsibility is
 * to get the sub details and render the appropriate route.
 */
export function Onboarding() {
  /*** Hooks ***/
  const useAuthObject = useAuth();

  /*** Render #1 - Not Authenticated */
  if (!isSubLoggedIn(useAuthObject)) {
    return <Redirect to={UNAUTHENTICATED_ROUTES["login"]} />;
  }

  /*** Constants ***/
  const sub = useAuthObject.userInfo;
  const onboardingConfigurationId = sub.onboardingConfigurationId;

  /*** Render #2 - Authenticated ***/
  return (
    <Switch>
      <Route exact path={ONBOARDING_ROUTES["more"]}>
        <MorePage />
      </Route>

      <OnboardingRoute
        exact
        path={ONBOARDING_ROUTES["tasks"]}
        rules={[
          isNotRejectedOrWaitlisted({ sub }),
          {
            // Render this route when there is a matching `onboardingConfigurationId`
            rule: (routeProps) =>
              routeProps.match?.params?.onboardingConfigurationId === onboardingConfigurationId,
            // Otherwise redirect to this page using the correct `onboardingConfigurationId`.
            redirectProps: () => ({
              to: generatePath(ONBOARDING_ROUTES["tasks"], {
                onboardingConfigurationId,
              }),
            }),
          },
        ]}
        render={(routeProps) => <TasksPage {...routeProps} />}
      />

      <OnboardingRoute
        exact
        path={ONBOARDING_ROUTES["rejection"]}
        rules={[
          {
            // Render this route when the sub IS Rejected or Waitlisted
            rule: () =>
              sub.onboardingStatus === "REJECTED" || sub.onboardingStatus === "WAITLISTED",
            // Otherwise, redirect to the tasks page using the correct `onboardingConfigurationId`.
            redirectProps: () => ({
              to: generatePath(ONBOARDING_ROUTES["tasks"], {
                onboardingConfigurationId,
              }),
            }),
          },
          {
            // Render this route when there is a matching `onboardingConfigurationId`
            rule: (routeProps) =>
              routeProps.match?.params?.onboardingConfigurationId === onboardingConfigurationId,
            // Otherwise, redirect to this page using the correct `onboardingConfigurationId`.
            redirectProps: () => ({
              to: generatePath(ONBOARDING_ROUTES["rejection"], {
                onboardingConfigurationId,
              }),
            }),
          },
        ]}
        render={(routeProps) => <RejectionPage {...routeProps} />}
      />

      <OnboardingRoute
        exact
        path={ONBOARDING_ROUTES["task"]}
        rules={[
          isNotRejectedOrWaitlisted({ sub }),
          {
            // Render this route when there is a matching `onboardingConfigurationId`
            rule: (routeProps) =>
              routeProps.match?.params?.onboardingConfigurationId === onboardingConfigurationId,
            // Otherwise, redirect to this page using the correct `onboardingConfigurationId`.
            redirectProps: ({ match }) => ({
              to: generatePath(ONBOARDING_ROUTES["task"], {
                onboardingConfigurationId,
                task: match.params.task,
              }),
            }),
          },
        ]}
        render={(routeProps) => <TaskPage {...routeProps} />}
      />

      {/* FALLBACK */}
      <Redirect
        // Generate the appropriate onboarding url for the sub.
        to={generatePath(ONBOARDING_ROUTES["tasks"], {
          onboardingConfigurationId,
        })}
      />
    </Switch>
  );
}

/********** Helper Functions **********/
type OnboardingRouteProps<Path extends string> = RouteProps<Path> & {
  // @ts-expect-error - @KoltonG having a bit of difficulty making this one happy
  render: (props: RouteComponentProps<ExtractRouteParams<Path, string>>) => ReactNode;
  rules?: Array<{
    // @ts-expect-error - @KoltonG having a bit of difficulty making this one happy
    rule: (routeProps: RouteComponentProps<ExtractRouteParams<Path, string>>) => boolean;
    redirectProps: (
      // @ts-expect-error - @KoltonG having a bit of difficulty making this one happy
      routeProps: RouteComponentProps<ExtractRouteParams<Path, string>>,
    ) => RedirectProps;
  }>;
};

/**
 * Call the `render` prop when all `rules` are met, otherwise calls the
 * redirects using teh `redirectProps` function.
 */
function OnboardingRoute<Path extends string>(props: OnboardingRouteProps<Path>) {
  const { rules, render: renderComponent, ...routeProps } = props;

  /***** Render ******/
  return (
    <Route
      {...routeProps}
      render={(_props) => {
        // NOTE: Upon redirect, the props.match.params seems to not be
        // calculating the params which causes a redirect loop. This const will
        // recompute the match params on each render to make sure it's the
        // latest.
        // @ts-expect-error - @KoltonG having a bit of difficulty making this
        // one happy
        const match = matchPath<ExtractRouteParams<Path, string>>(
          // @ts-expect-error - @KoltonG this field which is only defined at
          // runtime is the most consistent way to get the path. This is for
          // some reason not defined which is causing the type error.
          routeProps.computedMatch.url,
          routeProps,
        )!;
        const props = { ..._props, match };

        // NOTE: We are not calling `children` due to the above issue. We
        // noticed that when rendering `children` component, that components
        // hooks would not be capturing the latest `match.params` which would
        // cause issues. This approach allow us to pass in the latest
        // `match.params`.
        if (!rules) return renderComponent(props);

        // Iterate over all rules and redirect if it fails.
        for (const { rule, redirectProps } of rules) {
          if (!rule(props)) return <Redirect {...redirectProps(props)} />;
        }

        // When all rules pass, render the children
        // NOTE: We are not calling `children` due to the above issue. We noticed
        // that when rendering `children` component, that components hooks would
        // not be capturing the latest `match.params` which would cause issues.
        // This approach allow us to pass in the latest `match.params`.
        return renderComponent(props);
      }}
    />
  );
}

/***** Route Rules *****/
/**
 * A rule that checks if a Sub is NOT Rejected of Waitlisted, and if so,
 * redirects them to the appropriate Rejection page.
 */
function isNotRejectedOrWaitlisted(params: { sub: Models["Sub"] }) {
  const { sub } = params;

  return {
    rule: () => sub.onboardingStatus !== "REJECTED" && sub.onboardingStatus !== "WAITLISTED",
    // Otherwise redirect to the rejection page using the region.
    redirectProps: () => ({
      to: generatePath(ONBOARDING_ROUTES["rejection"], {
        onboardingConfigurationId: sub.onboardingConfigurationId,
      }),
    }),
  };
}
